import { Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import { categoryLabel } from "@/domains/freelance/taxonomy/store/taxonomy-store";
import { useOnboardingStore } from "../../context/OnboardingContext";
import { suggestedSubcategoryCount } from "../../utils/sector-step";

/**
 * The line over the buttons of the onboarding bar in the `sector` step (AC9):
 * «Andando avanti: Writing + Design & Creative · 3 ambiti dal CV». It keeps the
 * secondary readable when its section is below the fold. The count is the
 * subcategories still carrying the CV / LinkedIn suggestion; with no source, or
 * none left, the line is the sectors alone. Nothing until a primary is chosen.
 * No live region of its own: the step's announcer already says what changed.
 */
export const SectorBarSummary = () => {
  const [store] = useOnboardingStore();
  const { t } = useI18n();

  const sectors = () =>
    [store.sector.value.job_category, store.sector.value.job_category_secondary]
      .filter((key): key is NonNullable<typeof key> => !!key)
      .map(categoryLabel)
      .join(" + ");

  const fromSource = () => {
    const source = store.sector.source;
    if (!source) return null;
    const count = suggestedSubcategoryCount(store.sector.value);
    if (count === 0) return null;
    const from = t(
      source === "cv"
        ? "onboarding.steps.sector.summary.fromCv"
        : "onboarding.steps.sector.summary.fromLinkedin",
    );
    return count === 1
      ? t("onboarding.steps.sector.summary.count_one", { from })
      : t("onboarding.steps.sector.summary.count", { count, from });
  };

  return (
    <Show when={store.sector.value.job_category}>
      <p class="pb-3 text-xs text-foreground/70">
        {t("onboarding.steps.sector.nextVerb")}: {sectors()}
        <Show when={fromSource()}>{(text) => <> · {text()}</>}</Show>
      </p>
    </Show>
  );
};
